import type { Manifest, Pin, Memory } from '@/types';

// Client-side unlock evaluation. The server is source of truth for the
// gating (locked memory URLs return 423), but we re-evaluate locally so the
// UI doesn't show a "locked" pin a few seconds after unlock_at has passed.
//
// The signature returns the manifest as-is unless time has crossed an
// unlock_at; in that case it flips state on the affected pin/memory.

export function applyClientClock(m: Manifest, now: number = Date.now()): Manifest {
  let mutated = false;
  const pins = m.pins.map((pin) => {
    const flipped = applyToPin(pin, now);
    if (flipped !== pin) mutated = true;
    return flipped;
  });
  if (!mutated) return m;
  return { ...m, pins };
}

function applyToPin(pin: Pin, now: number): Pin {
  let nextState = pin.current_state;
  if (pin.current_state === 'locked' && pin.unlock_kind === 'time' && pin.unlock_at) {
    if (Date.parse(pin.unlock_at) <= now) nextState = 'unlocked';
  }
  const memories = pin.memories.map((m) => applyToMemory(m, now, nextState === 'unlocked' ? false : true));
  if (nextState === pin.current_state && memories === pin.memories) return pin;
  return { ...pin, current_state: nextState, memories };
}

function applyToMemory(m: Memory, now: number, parentLocked: boolean): Memory {
  const memUnlocked = !m.unlock_at || Date.parse(m.unlock_at) <= now;
  const locked = parentLocked || !memUnlocked;
  if (locked === m.locked) return m;
  return { ...m, locked };
}

/**
 * The next moment the UI should re-render. Used to schedule a single setTimeout
 * instead of polling every second — at any time only one upcoming unlock
 * matters, and once it fires we re-derive.
 */
export function nextUnlockMoment(m: Manifest, now: number = Date.now()): number | null {
  let earliest = Infinity;
  for (const pin of m.pins) {
    if (pin.unlock_at) {
      const t = Date.parse(pin.unlock_at);
      if (t > now && t < earliest) earliest = t;
    }
    for (const mem of pin.memories) {
      if (mem.unlock_at) {
        const t = Date.parse(mem.unlock_at);
        if (t > now && t < earliest) earliest = t;
      }
    }
  }
  return earliest === Infinity ? null : earliest;
}
